<template>
  <FriedFieldSet
    :label="label"
    active
    :error-message="internalError || errorMessage"
    :help-message="helpMessage"
    class="phone-number-input"
  >
    <FriedSelect
      v-model="countryCode"
      :data-external="`${dataExternal}-country-code-select`"
      aria-label="Country code"
      class="country-code-select"
      :options="countryCodesList"
      option-label="label"
      options-selected-label="flag"
      :disabled="disabled"
      append-to-body
      option-key="key"
      :same-width-as-ref="false"
    />
    <FriedInput
      ref="phoneInput"
      v-model="partialNumber"
      :data-external="`${dataExternal}-number-input`"
      :prefix="prefix"
      type="tel"
      :disabled="disabled"
      class="number-input"
      @paste.prevent="handlePaste"
    />
  </FriedFieldSet>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import type { CountryCode, PhoneNumber } from 'libphonenumber-js';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useI18n } from 'vue-i18n';
import { countryCodes } from '@getaccept/lib-shared/src/countries/constants/country-codes';
import type { CountryCodeObj } from '@getaccept/lib-shared/src/countries/constants/country-code';

const { t } = useI18n();

const countryCodesList = computed(() =>
  countryCodes.map((country: CountryCodeObj) => ({
    key: country.key,
    label: `${country.flag} (${country.code}) ${country.label} `,
    code: country.code,
    flag: country.flag,
  }))
);

const props = withDefaults(
  defineProps<{
    modelValue: string;
    label?: string;
    helpMessage?: string;
    errorMessage?: string;
    defaultCountryCode?: string;
    disabled?: boolean;
    required?: boolean;
    dataExternal?: string;
  }>(),
  {
    label: '',
    helpMessage: '',
    errorMessage: '',
    defaultCountryCode: undefined,
    dataExternal: 'contact-phone',
  }
);
const emit = defineEmits<{
  'update:model-value': [value: string];
  error: [error: string];
}>();

const partialNumber = ref('');
const countryCode = ref(props.defaultCountryCode || 'SE');
const phoneInput = ref(null);
const selectedCountryCode = computed(() =>
  countryCodesList.value.find(country => country.key === countryCode.value)
);
const prefix = computed(() => selectedCountryCode.value?.code);

const setPhoneNumberValues = (phoneNumber: string) => {
  if (!phoneNumber) {
    partialNumber.value = '';
    return;
  }
  const numberObject = parsePhoneNumberFromString(phoneNumber, countryCode.value as CountryCode);
  if (!numberObject) {
    partialNumber.value = phoneNumber;
    return;
  }
  partialNumber.value = numberObject.nationalNumber.toString();
  if (numberObject.country) {
    countryCode.value = numberObject.country;
  }
};

const handlePaste = (e: ClipboardEvent) => {
  setPhoneNumberValues(e.clipboardData?.getData('text') || '');
};

const validatePhoneNumber = (phoneNumber: PhoneNumber) => {
  if (!partialNumber.value && !props.required) {
    internalError.value = '';
    return;
  }
  internalError.value = !phoneNumber?.isValid() ? t('enter-a-valid-phone-number') : '';
};
const internalError = ref('');

watch(countryCode, () => {
  phoneInput.value?.focus();
});

watch(
  () => props.modelValue,
  () => {
    setPhoneNumberValues(props.modelValue);
  },
  { immediate: true }
);

watch([partialNumber, countryCode], () => {
  const numberObject = parsePhoneNumberFromString(
    partialNumber.value,
    countryCode.value as CountryCode
  );

  validatePhoneNumber(numberObject);

  if (numberObject?.number) {
    emit('update:model-value', numberObject.number);
    return;
  }
  if (!partialNumber.value) {
    emit('update:model-value', '');
    return;
  }
  emit('update:model-value', partialNumber.value);
});

watch(
  internalError,
  () => {
    emit('error', internalError.value);
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.phone-number-input {
  display: inline-block;
}

.number-input {
  width: 100%;

  :deep(.prefix) {
    padding-left: 0 !important;
  }

  :deep(fieldset) {
    border: none !important;
  }

  :deep(.input) {
    padding-left: var(--spacing-25) !important;
  }
}

.country-code-select {
  width: 80px;

  :deep(.icon-container) {
    padding-right: var(--spacing-50);
  }

  :deep(.input) {
    padding-right: var(--spacing-50) !important;
  }

  :deep(.input-outer-container) {
    fieldset {
      border: none !important;
    }
  }
}
</style>
